import type { ReactNode } from 'react'
import { Button } from '@repo/ui/components/ui/button'
import { SidebarMenuButton } from '@repo/ui/components/ui/sidebar'
import { Input } from '@repo/ui/components/ui/input'
import { Label } from '@repo/ui/components/ui/label'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@repo/ui/components/ui/sheet'
import { Textarea } from '@repo/ui/components/ui/textarea'
import { cn } from '@repo/ui/lib/utils'
import { HelpCircle, LifeBuoy, MessageCircle, Send, Sparkles } from 'lucide-react'
import { useState } from 'react'
import { useOnboarding } from '@/lib/onboarding'
import { APP_CONFIG } from '@/lib/config/constants'
import { validateFeedback } from '@/lib/services/feedback'
import { trpc } from '@/lib/trpc'
import { useFormSubmission } from '@/hooks/useFormSubmission'
import { type FaqEntry } from './types'

interface FAQProps {
  faqs: FaqEntry[]
  variant?: 'icon' | 'sidebar'
  label?: string
  trigger?: ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

const ROADMAP_URL = APP_CONFIG.EXTERNAL_URLS.ROADMAP

export function FAQ({
  faqs,
  variant = 'icon',
  label = 'Help & Feedback',
  trigger,
  open: openProp,
  onOpenChange,
}: FAQProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const { markAutoComplete } = useOnboarding()
  const sendFeedback = trpc.contact.send.useMutation()
  const open = openProp ?? uncontrolledOpen
  const handleOpenChange = (nextOpen: boolean) => {
    if (openProp === undefined) {
      setUncontrolledOpen(nextOpen)
    }
    onOpenChange?.(nextOpen)
  }

  const { data: feedback, setData, isSubmitting, isSubmitted, submit } = useFormSubmission(
    {
      email: '',
      message: '',
    },
    {
      submitFn: async (data) => {
        const email = data.email.trim() || 'anonymous@local.dev'
        const name = email.split('@')[0] || 'Anonymous'
        const result = await sendFeedback.mutateAsync({
          name,
          email,
          subject: 'Product feedback',
          message: data.message,
          source: 'operator_help',
        })
        if (!result.success) {
          throw new Error('Unable to send feedback')
        }
      },
      validateFn: validateFeedback,
      analyticsEvent: 'feedback_submitted',
      analyticsProps: (data) => ({
        message_length: data.message.length,
        has_email: Boolean(data.email.trim()),
      }),
      resetFn: () => ({ email: '', message: '' }),
      onSuccess: () => {
        markAutoComplete('share-feedback').catch(() => undefined)
      },
    }
  )

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      {trigger || openProp === undefined ? (
        <SheetTrigger asChild>
          {trigger ?? (variant === 'sidebar' ? (
            <SidebarMenuButton
              tooltip={label}
              className="cursor-pointer rounded-lg border border-transparent hover:border-sidebar-border/70"
              aria-label={label}
            >
              <LifeBuoy className="h-4 w-4" />
              <span>{label}</span>
            </SidebarMenuButton>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              className="cursor-pointer rounded-full"
              aria-label={label}
            >
              <HelpCircle className="h-5 w-5" />
            </Button>
          ))}
        </SheetTrigger>
      ) : null}
      <SheetContent side="right" className="w-full sm:max-w-md">
        <SheetHeader className="pb-0">
          <SheetTitle className="flex items-center gap-2 text-lg">
            <LifeBuoy className="h-5 w-5 text-primary" />
            {label}
          </SheetTitle>
          <SheetDescription>
            Browse quick answers, send feedback, or see what&apos;s planned next.
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-1 flex-col gap-6 overflow-y-auto p-4 pt-2">
          <section>
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              FAQs
            </h3>
            <ul className="mt-3 space-y-3">
              {faqs.map((faq) => (
                <li
                  key={faq.question}
                  className="rounded-lg border border-border/50 bg-muted/40 p-3"
                >
                  <p className="font-medium text-sm">{faq.question}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{faq.answer}</p>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Feedback
              </h3>
              {isSubmitted && (
                <span className="text-xs font-medium text-primary">Thanks for the note!</span>
              )}
            </div>
            <form onSubmit={submit} className="mt-3 space-y-3">
              <div className="space-y-1">
                <Label className="text-xs font-medium text-muted-foreground" htmlFor="help-email">
                  Reply-to (optional)
                </Label>
                <Input
                  id="help-email"
                  type="email"
                  placeholder="you@example.com"
                  value={feedback.email}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setData({ email: event.target.value })
                  }
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-muted-foreground" htmlFor="help-message">
                  What&apos;s on your mind?
                </Label>
                <Textarea
                  id="help-message"
                  required
                  minLength={8}
                  rows={4}
                  value={feedback.message}
                  onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setData({ message: event.target.value })
                  }
                  placeholder="Share feedback, a bug report, or feature request."
                  className="min-h-0"
                />
              </div>
              <Button
                type="submit"
                className="flex w-full items-center justify-center gap-2"
                disabled={isSubmitting}
              >
                <Send className={cn('h-4 w-4', isSubmitting && 'animate-pulse')} />
                {isSubmitting ? 'Sending...' : 'Send feedback'}
              </Button>
            </form>
          </section>

          <section className="rounded-lg border border-dashed border-primary/40 bg-primary/5 p-4">
            <div className="flex items-start gap-3">
              <Sparkles className="mt-1 h-5 w-5 text-primary" />
              <div className="space-y-2 text-sm">
                <p className="font-medium text-primary">See what&apos;s coming next</p>
                <p className="text-muted-foreground">
                  Follow the public roadmap or leave feature requests—votes help us prioritise the
                  right things.
                </p>
                <Button variant="outline" size="sm" asChild>
                  <a href={ROADMAP_URL} target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="mr-2 h-4 w-4" />
                    Open roadmap
                  </a>
                </Button>
              </div>
            </div>
          </section>
        </div>
      </SheetContent>
    </Sheet>
  )
}

export { type FaqEntry } from './types'
